import { buildInfo, shortCommit } from '@brush/build-info';
import { takeErasedNotice } from '../erasure';
import { themeFor } from '../theme';

/**
 * The news that an account was just erased (ADR 0012 step 28), read once per page load: the erasure
 * signs out, and the sign-out returns here. Read at module scope rather than in a state initialiser,
 * which StrictMode calls twice — and the second call, finding the notice already taken, would win.
 */
const ERASED_NOTICE: string | null = (() => {
  try {
    return takeErasedNotice(window.sessionStorage);
  } catch {
    return null;
  }
})();

/**
 * The door. Direction C — "The Rail", on-air side, before anybody has signed in.
 *
 * A split: the brand at full size on the left, and one column on the right that does exactly one
 * thing. It deliberately does *not* wear `PortalShell`'s rail — there is no navigation to hang off
 * one, and a 56px chrome bar above a screen with a single control is furniture. The mark is the
 * chrome here, which is why it carries the same notch, cut to the same proportion.
 *
 * <b>The call to action is orange</b>, matching `ConnectCard`, `CardsScreen`, `PlayerPicker` and
 * `LineupBuilder`. `rail.css` says signal orange means "this is you" here and is never a control —
 * true of the ticker, the standings and every self-referential figure — but the portal's *primary
 * buttons* have always been `background: theme.accent, color: theme.bg`, and the claim flow this
 * button opens onto is one of them. The rule still holds where it is load-bearing: nothing
 * informational on this page is orange, and there is no signed-in player for it to be about.
 * If the direction is ever enforced properly, this button and those four move together.
 *
 * Presentational, and deliberately fetches nothing. The only endpoints reachable without a token
 * are the room directory and `/version`; the door cannot know which room somebody plays at, since
 * a room is resolved from a membership after sign-in. Hence "every room" rather than a name.
 */
export function LandingScreen({ onSignIn }: { onSignIn: () => void }) {
  const theme = themeFor(null);

  return (
    <div
      className="onair relative flex min-h-dvh flex-col overflow-hidden"
      style={{ background: theme.bg, color: theme.text }}
    >
      {/*
        The composition is capped and centred rather than filling the window. It was drawn at
        1440, where a 720px left panel *is* half the screen — left to fill, that same 720px is a
        third of an ultrawide and the whole door huddles against the left edge with a third of a
        metre of empty ground beside it. Capping keeps the drawing's proportions at every width
        above it; `w-1/2` rather than a fixed 720 keeps them below it too, where a fixed panel
        would leave the right column less room than it asks for and quietly squeeze it.

        <b>Below `lg` it is one centred stack, on the pattern of a phone sign-in</b> — the mark as
        artwork in the upper half, one short headline, one action, and the fine print at the foot.
        The first phone layout stacked the desktop's two halves (a band, then a column centred in
        what was left), which left a hole between them and read as two screens; the second fixed
        the hole but kept everything left-aligned and at nearly desktop size, so the page still read
        as a desktop column squeezed onto glass.
      */}
      <div className="relative mx-auto flex w-full max-w-[1440px] flex-1 flex-col justify-center pb-8 pt-10 short:pb-4 short:pt-4 lg:flex-row lg:justify-normal lg:py-0">
        {/*
          Half the composition from `lg`; below it, the artwork at the head of the stack. The
          reference phone is 430px, where none of the `lg:` sizes apply at all — so the mark, the
          spade and the notch are each sized twice rather than scaled.

          On a phone the box is exactly the spade's height and does not clip, so the spade sits
          whole behind the mark the way it does on desktop. It used to be cropped flat by a band,
          which drew a faint box with a hard lower edge across the page.
        */}
        <div className="relative flex h-[240px] shrink-0 items-center justify-center short:h-[132px] lg:h-auto lg:w-1/2 lg:overflow-hidden">
          <svg
            viewBox="0 0 24 24"
            className="pointer-events-none absolute left-1/2 top-1/2 h-[240px] w-[240px] max-w-none -translate-x-1/2 -translate-y-1/2 opacity-[0.035] short:h-[150px] short:w-[150px] lg:h-[560px] lg:w-[560px] lg:opacity-[0.022]"
            aria-hidden="true"
          >
            <path
              d="M12 2C12 2 3.6 8.7 3.6 13.8a4.3 4.3 0 0 0 7.4 3c-.2 2.5-1 4.2-2.3 5.2h6.6c-1.3-1-2.1-2.7-2.3-5.2a4.3 4.3 0 0 0 7.4-3C20.4 8.7 12 2 12 2z"
              fill={theme.text}
            />
          </svg>

          {/*
            The notch is a custom property rather than a literal so it can change with the mark: a
            44px cut off a 280px block reads as a clipped corner, off a 560px one it reads as the
            same mark the header wears. An arbitrary-property utility emits a real rule, so unlike
            a `@theme` value it is not pruned for being read only from an inline style.
          */}
          <div
            className="relative flex h-[64px] items-center bg-signal pl-5 pr-7 [--notch:18px] short:h-[48px] short:pl-4 short:pr-6 short:[--notch:14px] lg:h-[132px] lg:pl-11 lg:pr-14 lg:[--notch:44px]"
            style={{ clipPath: 'polygon(0 0, 100% 0, calc(100% - var(--notch)) 100%, 0 100%)' }}
          >
            <span className="text-[34px] font-bold italic leading-none tracking-[0.01em] text-white short:text-[26px] lg:text-[76px]">
              BRUSH
            </span>
          </div>
        </div>

        <div className="flex px-6 lg:min-w-0 lg:flex-1 lg:items-center lg:py-0 lg:pl-10 lg:pr-24">
          <div className="mx-auto flex w-full max-w-[400px] flex-col text-center lg:mx-0 lg:max-w-[430px] lg:text-left">
            {/*
              Broken at the comma rather than left to wrap. At 58px the line lands mid-phrase on
              its own, and where it lands then changes with the viewport — the two-line block is
              the composition's counterweight to the mark on the left, so it is set rather than
              discovered.
            */}
            {ERASED_NOTICE ? (
              <p
                role="status"
                className="mb-6 p-3 text-left text-[13px] leading-[1.5]"
                style={{ border: `1px solid ${theme.border}`, color: theme.text }}
              >
                {ERASED_NOTICE}
              </p>
            ) : null}

            <h1 className="text-[30px] font-semibold uppercase leading-[1.05] tracking-[0.005em] short:text-[22px] lg:text-[58px] lg:leading-[1.02]">
              All Your Cards,
              <br />
              Every Room
            </h1>

            {/*
              Desktop only. On a phone it says exactly what the button beneath it says, and the
              stack is short enough that a heading over a single control is furniture.
            */}
            <h2 className="mt-12 hidden text-[24px] font-semibold uppercase leading-[1.1] tracking-[0.02em] lg:block">
              Sign in to Brush
            </h2>

            <button
              type="button"
              onClick={onSignIn}
              className="mt-8 flex min-h-[48px] w-full items-center justify-center text-[15px] font-semibold uppercase tracking-[0.09em] transition hover:opacity-90 short:mt-5 lg:mt-5 lg:min-h-[54px] lg:text-[16px]"
              style={{ background: theme.accent, color: theme.bg }}
            >
              Sign in
            </button>

            {/*
              The mockup's second half — "By signing in you agree to the Terms and Privacy Policy",
              both linked — is deliberately not here. Those pages do not exist, so the links were
              `href="#"`, and unlike a dead nav link this one asserts agreement to a document nobody
              can read. Same call §K10 already makes for the Entra sign-in page's footer, which
              hides Privacy & Cookies and Terms of Use for the same reason and is on the "Before
              production" checklist in `docs/specs/player-authentication-implementation-plan.md`.
              It goes back, as written, the moment there is somewhere for it to point.
            */}
            <p className="mt-3.5 text-[13px] leading-[1.6]" style={{ color: theme.textMuted }}>
              No password to create or remember.
            </p>

            <BuildStamp color={theme.textMuted} className="mt-10 hidden lg:block" />
          </div>
        </div>
      </div>

      {/*
        The phone's copy of the stamp, at the foot of the screen where fine print lives, rather
        than trailing the button as part of what the page is asking somebody to do.
      */}
      <BuildStamp color={theme.textMuted} className="pb-6 text-center short:pb-3 lg:hidden" />
    </div>
  );
}

/**
 * Not `VersionBadge`, and nothing opens behind it: the About dialog reports the API's version too,
 * and the client that can ask for it does not exist before sign-in. Half the answer is worth
 * having — known gap 8's failure mode is a clean clone with no `.env` looking exactly like a broken
 * app *on this screen*, and the build number is the first useful thing to read when it does.
 */
function BuildStamp({ color, className }: { color: string; className: string }) {
  return (
    <span className={`num text-[10.5px] tracking-[0.06em] ${className}`} style={{ color }}>
      v{buildInfo.version}
      {buildInfo.commit ? <span> · {shortCommit(buildInfo.commit)}</span> : null}
    </span>
  );
}
